import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';

type Alert = {
  id: string;
  fleetId: string;
  fleetName: string;
  title: string;
  status: string;
  dueDate?: string | null;
  dueMileage?: number | null;
  licensePlate?: string | null;
};

const FleetCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState('30');
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/fleets/calendar', { params: { days } });
      setAlerts((response.data?.alerts || []) as Alert[]);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo cargar el calendario');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = useMemo(() => {
    return alerts.reduce(
      (acc, a) => {
        if (a.status === 'vencida') acc.overdue += 1;
        else if (a.status === 'proxima') acc.upcoming += 1;
        else acc.pending += 1;
        return acc;
      },
      { overdue: 0, upcoming: 0, pending: 0 }
    );
  }, [alerts]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Calendario de flotas</h1>
          <p className="text-sm text-muted-foreground">Vencimientos por fecha y kilometraje</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/fleets')}>Volver</Button>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Alertas</div><div className="text-2xl font-bold">{alerts.length}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Vencidas</div><div className="text-2xl font-bold text-red-600">{summary.overdue}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Próximas</div><div className="text-2xl font-bold">{summary.upcoming}</div></CardContent></Card>
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="text-xs text-muted-foreground">Rango de días</div>
            <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="15">15 días</option><option value="30">30 días</option><option value="45">45 días</option><option value="60">60 días</option><option value="90">90 días</option>
            </select>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Alertas por vencimiento</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Flota</TableHead><TableHead>Título</TableHead><TableHead>Unidad</TableHead><TableHead>Vence</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acción</TableHead></TableRow></TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={6}>Cargando...</TableCell></TableRow>}
                {!loading && alerts.length === 0 && <TableRow><TableCell colSpan={6}>Sin alertas en el rango</TableCell></TableRow>}
                {!loading && alerts.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.fleetName}</TableCell>
                    <TableCell>{a.title}</TableCell>
                    <TableCell>{a.licensePlate || '-'}</TableCell>
                    <TableCell>{a.dueDate || (a.dueMileage ? `${a.dueMileage} km` : '-')}</TableCell>
                    <TableCell><Badge variant={a.status === 'vencida' ? 'destructive' : 'outline'}>{a.status}</Badge></TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => navigate(`/fleets/${a.fleetId}`)}>Abrir</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FleetCalendar;
